import { useEventCallback } from '@mui/material/utils';
import { FC, PropsWithChildren, useMemo, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { AccessTokenContext, accessTokenContextDefaultValue, AccessTokenContextValue } from './AccessTokenContext';
import { useRefreshWithRetries } from '../hooks/useRefreshWithRetries';
import { setRedirectUri } from '../../utils/redirect-uri';
import { jwtDecode } from '../../utils/jwt-decode';

export const AccessTokenProvider: FC<PropsWithChildren<{}>> = ({ children }) => {
  const auth = useAuth();
  const authIsLoading = auth.isLoading;
  const authJwt = auth.user?.access_token;
  const [timedOutJwt, setTimedOutJwt] = useState<string | undefined>();

  useRefreshWithRetries();

  const signIn = useEventCallback<Required<AccessTokenContextValue>['signIn']>(
    async (path, accountId, orgId) => {
      if (auth.isLoading) return;
      if (typeof path !== 'undefined') setRedirectUri(path);
      const extraQueryParams: Record<string, string> = {};
      if (accountId) {
        extraQueryParams.aid = accountId;
      }
      if (orgId) {
        extraQueryParams.oid = orgId;
      }
      if (path?.pathname === '/welcome') {
        extraQueryParams.k_prompt = 'account';
      }
      await auth.signinRedirect({
        extraQueryParams,
      });
      setTimedOutJwt(undefined);
    },
  );
  const signOut = useEventCallback<Required<AccessTokenContextValue>['signOut']
  >(async path => {
    if (auth.isLoading) return;
    if (typeof path !== 'undefined') setRedirectUri(path);
    await auth.signoutRedirect();
  });
  const signOutTimedOut = useEventCallback(async () => {
    const jwt = authJwt;
    await auth.revokeTokens();
    await auth.removeUser();
    setTimedOutJwt(jwt);
  });

  const prevValue = useRef(accessTokenContextDefaultValue);
  const value = useMemo<AccessTokenContextValue>(() => {
    const jwt = timedOutJwt ?? authJwt;
    if (typeof jwt === 'undefined') {
      if (
        authIsLoading
        && prevValue.current === accessTokenContextDefaultValue
      ) {
        // if this is the initial loading -> return the default value
        // once it's loaded, prev value will be set to something else
        // (either `idle` or `signedIn`, dpending on the actual session data)
        // the default value will never be returned anymore
        return accessTokenContextDefaultValue;
      }

      return { status: 'idle', signIn };
    }

    const [payload, jwtError] = jwtDecode(jwt);
    if (typeof jwtError !== 'undefined') {
      return { status: 'error', error: jwtError, signIn };
    }

    const accountUuid = payload?.account_uuid ?? '';
    const email = payload?.email ?? '';
    const sub = payload?.sub ?? '';
    if (jwt === timedOutJwt) {
      return {
        status: 'timedOut',
        accountUuid,
        email,
        jwt,
        signIn,
        source: 'oidc',
        sub,
      };
    }

    return {
      status: 'signedIn',
      accountUuid,
      email,
      jwt,
      signOut,
      signOutTimedOut,
      source: 'oidc',
      sub,
    };
  }, [authIsLoading, authJwt, signIn, signOut, signOutTimedOut, timedOutJwt]);
  prevValue.current = value;

  return (
    <AccessTokenContext.Provider value={value}>
      {children}
    </AccessTokenContext.Provider>
  );
};
